import { createSlice } from '@reduxjs/toolkit'
import { SelectionType } from 'utils/constant/filterStatus'
import { ErrorData } from 'features/error/errorSlice'
import { RootState } from 'app/store'
import { TripConfigData } from 'features/settings/tripDetailSettingSlice'
import {
  FoodPolicyScope,
  PolicyScope,
  TransportPolicyScope
} from 'features/policies/policySlice'

type CateringScope = 'catering'
type BookingScopes = PolicyScope | CateringScope

export interface FareBreakDownData {
  amount: number
  description: string
  display_name: string
  type: string
}

export interface FoodPricingInfo {
  v1: {
    payment_method_details: PaymentMethodDetails
    line_items: LineItems
  }
}

export interface LineItems {
  rows: Array<LineItem>
}

export interface LineItem {
  id: string
  title: string
  value: string
  prev_value: string
}

export interface PaymentMethodDetails {
  total: string
  prev_total: string
  methods: Array<PaymentMethod>
  edit_info: PaymentMerchantEditInfo
}

export interface PaymentMethod {
  amount: string
  method: string
  display_name: string
  network: string
}

export interface PaymentMerchantEditInfo {
  icon_url: string
  text: string
}

export interface ShoppingItem {
  quantity: number
  name: string
}

export interface BookingDestinationData {
  id?: string
  address: string
  address_details: string
  location_name: string
  distance_in_kms: number
  name?: string
  received_by?: string
  pickup_time: string
  type: string
}

export interface BookingProofData {
  reference_id: string
  address: string
  address_details: string
  location_name: string
  distance_in_kms?: number
  url: string
  received_by?: string
  pickup_time?: string
  completed_at?: string
}

export interface BookingData {
  id: number
  booking_creation_time: string
  base_fare: number
  booking_completion_time: string
  total_distance_in_kms: number
  drop: string
  employee_name: string
  employee_number: string
  company_name: string
  group_name: string
  order_number: string
  pickup: string
  pricing_currency: string
  service_type: string
  fare_break_down: Array<FareBreakDownData>
  total_fare: number
  scope: BookingScopes
  destinations: Array<BookingDestinationData>
  vehicle_plate_number: string
  voucher_code: string
  voucher_program_name: string
  delivery_note: string
}

export interface TransportBookingData extends BookingData {
  scope: TransportPolicyScope
  business_booking: {
    trip_reason: string
    trip_code: string
    batch_name: string
    trip_description: string
    ride_share: number
  }
  passenger_name: string
  passenger_phone: string
}

export interface FoodBookingData extends BookingData {
  scope: FoodPolicyScope
  food_details: {
    shopping_items: Array<ShoppingItem>
    pricing_info: FoodPricingInfo
  }
}

export interface CateringBookingData extends BookingData {
  scope: CateringScope
  food_details: {
    shopping_items: Array<ShoppingItem>
    pricing_info: FoodPricingInfo
  }
}

type EmbeddedTripConfigData = Pick<
  TripConfigData,
  'ride_share_info_required' | 'trip_code_required'
>

export interface TripState {
  isLoading: boolean
  trips: Array<BookingData>
  tripConfig: EmbeddedTripConfigData
  total: number
  checkedTripIds: Array<number>
  itemsPerPage: number
  currentPage: number
  filters: {
    groupIds: Array<string>
    companyIds: Array<string>
    fromTime: string | null
    toTime: string | null
    query: string
    scope: Array<string>
    usage: Array<string>
    platform: Array<string>
  }
  isAllPagesSelected: boolean
  isFilterActive: boolean
  errors: ErrorData
}

export const initialState: TripState = {
  isLoading: false,
  trips: [],
  tripConfig: {
    trip_code_required: false,
    ride_share_info_required: false
  },
  total: 0,
  checkedTripIds: [],
  itemsPerPage: 10,
  currentPage: 1,
  filters: {
    groupIds: [],
    companyIds: [],
    fromTime: null,
    toTime: null,
    query: '',
    scope: [],
    usage: [],
    platform: []
  },
  isAllPagesSelected: false,
  isFilterActive: false,
  errors: null
}

export const tripSlice = createSlice({
  name: 'trip',
  initialState: initialState,
  reducers: {
    setFilterGroup: (state, action) => {
      const { payload: value } = action
      const {
        filters: { groupIds }
      } = state
      if (value === SelectionType.ALL) {
        state.filters.groupIds = initialState.filters.groupIds
      } else if (groupIds.includes(value)) {
        let filterGroup = groupIds.filter((id) => id !== value)
        state.filters.groupIds = filterGroup
      } else {
        state.filters.groupIds = [...groupIds, value]
      }
    },
    setFilterCompany: (state, action) => {
      const { payload: value } = action
      const {
        filters: { companyIds }
      } = state
      if (companyIds.includes(value)) {
        let filterCompanyIds = companyIds.filter((id) => id !== value)
        state.filters.companyIds = filterCompanyIds
      } else {
        state.filters.companyIds = [...companyIds, value]
      }
    },
    setFilterQuery: (state, action) => {
      state.filters = {
        ...initialState.filters,
        query: action.payload
      }
      state.currentPage = initialState.currentPage
    },
    setFilter: (state, action) => {
      const { payload } = action
      if (!payload) return
      state.filters = {
        ...state.filters,
        ...payload
      }
      state.currentPage = initialState.currentPage
    },
    setFilterUsage: (state, action) => {
      const { payload: value } = action
      const {
        filters: { usage }
      } = state
      if (usage.includes(value)) {
        let filterUsage = usage.filter((id) => id !== value)
        state.filters.usage = filterUsage
      } else {
        state.filters.usage = [...usage, value]
      }
    },
    setFilterPlatform: (state, action) => {
      const { payload: value } = action
      const {
        filters: { platform }
      } = state
      if (platform.includes(value)) {
        let filterPlatform = platform.filter((id) => id !== value)
        state.filters.platform = filterPlatform
      } else {
        state.filters.platform = [...platform, value]
      }
    },
    setFilterScope: (state, action) => {
      const { payload: value } = action
      const {
        filters: { scope }
      } = state
      if (scope.includes(value)) {
        let filterScope = scope.filter((id) => id !== value)
        state.filters.scope = filterScope
      } else {
        state.filters.scope = [...scope, value]
      }
    },
    setFromTime: (state, action) => {
      state.filters.fromTime = action.payload
    },
    setToTime: (state, action) => {
      state.filters.toTime = action.payload
    },
    resetFilter: (state, action) => {
      if (!action.payload) {
        state.filters = initialState.filters
        return
      }
      const {
        groupIds,
        scope,
        companyIds,
        fromTime,
        toTime,
        query,
        usage,
        platform
      } = action.payload
      if (groupIds) {
        state.filters.groupIds = initialState.filters.groupIds
      }
      if (scope) {
        state.filters.scope = initialState.filters.scope
      }
      if (companyIds) {
        state.filters.companyIds = initialState.filters.companyIds
      }
      if (fromTime) {
        state.filters.fromTime = initialState.filters.fromTime
      }
      if (toTime) {
        state.filters.toTime = initialState.filters.toTime
      }
      if (query) {
        state.filters.query = initialState.filters.query
      }
      if (usage) {
        state.filters.usage = initialState.filters.usage
      }
      if (platform) {
        state.filters.platform = initialState.filters.platform
      }
      state.currentPage = initialState.currentPage
    },
    resetFilterGroup: (state) => {
      state.filters.groupIds = initialState.filters.groupIds
    },
    resetFilterScope: (state) => {
      state.filters.scope = initialState.filters.scope
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload
    },
    resetPagination: (state) => {
      state.itemsPerPage = initialState.itemsPerPage
      state.currentPage = initialState.currentPage
    },
    setCheckedTripIds: (state, action) => {
      state.checkedTripIds = action.payload
    },
    setAllPagesSelected: (state, action) => {
      if (state.isAllPagesSelected) {
        state.checkedTripIds = initialState.checkedTripIds
      }
      state.isAllPagesSelected = action.payload
    },
    setTripsData: (state, action) => {
      const { total, trip_config } = action.payload
      state.total = total
      if (trip_config) {
        state.tripConfig = trip_config
      }
    },
    resetState: () => initialState
  }
})

export const {
  setCheckedTripIds,
  setCurrentPage,
  setFromTime,
  setToTime,
  setItemsPerPage,
  setFilter,
  setFilterCompany,
  setFilterQuery,
  setFilterGroup,
  setFilterUsage,
  setFilterScope,
  setFilterPlatform,
  setAllPagesSelected,
  setTripsData,
  resetFilter,
  resetFilterGroup,
  resetFilterScope,
  resetPagination,
  resetState
} = tripSlice.actions

export const tripSelector = (state: RootState) => state.trip

export default tripSlice.reducer
